import { lazy, Suspense } from "react";
import { AppErrorBoundary } from "@/components/AppErrorBoundary";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "@/hooks/useAuth";
import Index from "./pages/Index.tsx";
import ScrollToTop from "./components/ScrollToTop";
import PageViewTracker from "./components/PageViewTracker";

const MobileMaintenance = lazy(() => import("./pages/MobileMaintenance.tsx"));
const CeramicCoating = lazy(() => import("./pages/CeramicCoating.tsx"));
const PPF = lazy(() => import("./pages/PPF.tsx"));
const WindowTint = lazy(() => import("./pages/WindowTint.tsx"));
const DetailingPaintCorrection = lazy(() => import("./pages/DetailingPaintCorrection.tsx"));
const BookNow = lazy(() => import("./pages/BookNow.tsx"));
const AboutUs = lazy(() => import("./pages/MeetTheTeam.tsx"));
const Protect = lazy(() => import("./pages/Protect.tsx"));
const Correct = lazy(() => import("./pages/Correct.tsx"));
const Shade = lazy(() => import("./pages/Shade.tsx"));
const Shine = lazy(() => import("./pages/Shine.tsx"));
const NotFound = lazy(() => import("./pages/NotFound.tsx"));
const InsightsHub = lazy(() => import("./pages/InsightsHub.tsx"));
const ResultsHub = lazy(() => import("./pages/ResultsHub.tsx"));
const ArticleDetail = lazy(() => import("./pages/ArticleDetail.tsx"));
const CaseStudyDetail = lazy(() => import("./pages/CaseStudyDetail.tsx"));
const Privacy = lazy(() => import("./pages/Privacy.tsx"));
const Terms = lazy(() => import("./pages/Terms.tsx"));

// Admin pages
const AdminLogin = lazy(() => import("./pages/AdminLogin.tsx"));
const AdminLayout = lazy(() => import("./pages/AdminLayout.tsx"));
const Dashboard = lazy(() => import("./pages/admin/Dashboard.tsx"));
const FAQsList = lazy(() => import("./pages/admin/FAQsList.tsx"));
const FAQEditor = lazy(() => import("./pages/admin/FAQEditor.tsx"));
const ArticlesList = lazy(() => import("./pages/admin/ArticlesList.tsx"));
const ArticleEditor = lazy(() => import("./pages/admin/ArticleEditor.tsx"));
const CaseStudiesList = lazy(() => import("./pages/admin/CaseStudiesList.tsx"));
const CaseStudyEditor = lazy(() => import("./pages/admin/CaseStudyEditor.tsx"));
const ServicesList = lazy(() => import("./pages/admin/ServicesList.tsx"));
const AuthorsList = lazy(() => import("./pages/admin/AuthorsList.tsx"));
const ChatAnalytics = lazy(() => import("./pages/admin/ChatAnalytics.tsx"));
const ChatConversations = lazy(() => import("./pages/admin/ChatConversations.tsx"));
const ReviewsManagement = lazy(() => import("./pages/admin/ReviewsManagement.tsx"));
const TopicsList = lazy(() => import("./pages/admin/TopicsList.tsx"));
const InsightsTopicsIndex = lazy(() => import("./pages/InsightsTopicsIndex.tsx"));
const SiteAnalytics = lazy(() => import("./pages/admin/SiteAnalytics.tsx"));

const queryClient = new QueryClient();

const routeLoadingFallback = (
  <div
    className="min-h-screen bg-background flex flex-col items-center justify-center gap-3 px-4"
    role="status"
    aria-live="polite"
  >
    <div className="h-9 w-9 rounded-full border-2 border-primary border-t-transparent animate-spin" aria-hidden />
    <span className="text-sm text-muted-foreground font-display uppercase tracking-wider">Loading…</span>
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <ScrollToTop />
          <PageViewTracker />
          <AppErrorBoundary>
            <Suspense fallback={routeLoadingFallback}>
              <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/mobile-maintenance" element={<MobileMaintenance />} />
              <Route path="/ceramic-coating" element={<CeramicCoating />} />
              <Route path="/ppf" element={<PPF />} />
              <Route path="/window-tint" element={<WindowTint />} />
              <Route path="/detailing-paint-correction" element={<DetailingPaintCorrection />} />
              <Route path="/book" element={<BookNow />} />
              <Route path="/about-us" element={<AboutUs />} />
              <Route path="/meet-the-team" element={<AboutUs />} />
              <Route path="/protect" element={<Protect />} />
              <Route path="/correct" element={<Correct />} />
              <Route path="/shade" element={<Shade />} />
              <Route path="/shine" element={<Shine />} />
              <Route path="/insights" element={<InsightsHub />} />
              <Route path="/insights/topics" element={<InsightsTopicsIndex />} />
              <Route path="/insights/:slug" element={<ArticleDetail />} />
              <Route path="/results" element={<ResultsHub />} />
              <Route path="/results/:slug" element={<CaseStudyDetail />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/terms" element={<Terms />} />

              {/* Admin CMS */}
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin/preview/article/:slug" element={<ArticleDetail preview />} />
              <Route path="/admin/preview/case-study/:slug" element={<CaseStudyDetail preview />} />
              <Route path="/admin" element={<AdminLayout />}>
                <Route index element={<Dashboard />} />
                <Route path="faqs" element={<FAQsList />} />
                <Route path="faqs/new" element={<FAQEditor />} />
                <Route path="faqs/:id" element={<FAQEditor />} />
                <Route path="articles" element={<ArticlesList />} />
                <Route path="articles/new" element={<ArticleEditor />} />
                <Route path="articles/:id" element={<ArticleEditor />} />
                <Route path="case-studies" element={<CaseStudiesList />} />
                <Route path="case-studies/new" element={<CaseStudyEditor />} />
                <Route path="case-studies/:id" element={<CaseStudyEditor />} />
                <Route path="services" element={<ServicesList />} />
                <Route path="authors" element={<AuthorsList />} />
                <Route path="chat" element={<ChatAnalytics />} />
                <Route path="chat/conversations" element={<ChatConversations />} />
                <Route path="reviews" element={<ReviewsManagement />} />
                <Route path="topics" element={<TopicsList />} />
                <Route path="analytics" element={<SiteAnalytics />} />
              </Route>

              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
            </Suspense>
          </AppErrorBoundary>
        </BrowserRouter>
      </TooltipProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
